// Área de envio de vídeo da lição. Decide entre o caminho DIRETO (browser ->
// Azure Blob, em blocos) e o PROXY multipart legado conforme a flag do backend.
//
// A flag vem do backend via /api/auth/me/, nunca de VITE_*: metade das VITE_*
// não chega ao build de produção — foi assim que o timeout de 30 s virou
// imutável dentro da imagem do frontend.

import { FC, useCallback, useRef, useState } from 'react';
import { useVideoUpload } from '@/hooks/useVideoUpload';
import { videoService } from '@/services/video.service';
import type { AttachedVideo } from '@/services/videoUpload/types';
import { formatVideoSize } from '@/utils/videoUploadError';

const ACCEPTED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const ACCEPT_ATTR = 'video/mp4,video/webm,video/quicktime';

interface VideoUploadPanelProps {
  lessonId: string;
  /** Caminho direto ligado no backend (kill-switch em runtime). */
  directEnabled: boolean;
  maxBytes: number;
  videoTitle: string;
  onUploaded: (video: AttachedVideo, lesson: unknown | null) => void;
  onBusyChange?: (busy: boolean) => void;
  onValidationError: (message: string) => void;
  onUploadError: (err: unknown, fileName: string) => void;
}

const VideoUploadPanel: FC<VideoUploadPanelProps> = ({
  lessonId,
  directEnabled,
  maxBytes,
  videoTitle,
  onUploaded,
  onBusyChange,
  onValidationError,
  onUploadError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [proxyBusy, setProxyBusy] = useState(false);
  const [proxyPercent, setProxyPercent] = useState(0);

  const direct = useVideoUpload({
    lessonId,
    onAttached: (video, lesson) => {
      onBusyChange?.(false);
      onUploaded(video, lesson);
    },
    onError: (err, name) => {
      onBusyChange?.(false);
      onUploadError(err, name);
    },
  });

  const maxLabel = formatVideoSize(maxBytes);

  // Cancelar não passa por onAttached nem por onError — sem soltar o "ocupado"
  // aqui, o botão Salvar da lição ficava travado para sempre depois de um
  // envio cancelado.
  const handleCancel = useCallback(() => {
    void direct.cancel();
    onBusyChange?.(false);
  }, [direct, onBusyChange]);

  const handleRetry = useCallback(() => {
    onBusyChange?.(true);
    void direct.retry(videoTitle);
  }, [direct, onBusyChange, videoTitle]);
  const busy = directEnabled ? direct.phase !== 'idle' && direct.phase !== 'error' : proxyBusy;
  const percent = directEnabled ? direct.percent : proxyPercent;

  const uploadViaProxy = useCallback(
    async (file: File) => {
      setProxyBusy(true);
      setProxyPercent(0);
      onBusyChange?.(true);
      try {
        const result = await videoService.uploadVideo(file, setProxyPercent);
        onUploaded(result, null);
      } catch (err) {
        onUploadError(err, file.name);
      } finally {
        setProxyBusy(false);
        setProxyPercent(0);
        onBusyChange?.(false);
      }
    },
    [onBusyChange, onUploadError, onUploaded]
  );

  const handleFile = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        onValidationError('Tipo não suportado. Use MP4, WebM ou MOV.');
        return;
      }
      if (file.size > maxBytes) {
        onValidationError(`Arquivo muito grande. Máximo: ${maxLabel}.`);
        return;
      }
      if (directEnabled) {
        onBusyChange?.(true);
        void direct.start(file, videoTitle);
      } else {
        void uploadViaProxy(file);
      }
      if (inputRef.current) inputRef.current.value = '';
    },
    [directEnabled, direct, maxBytes, maxLabel, onBusyChange, onValidationError, uploadViaProxy, videoTitle]
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (busy) {
    const label =
      direct.phase === 'preparing'
        ? 'Preparando envio...'
        : direct.phase === 'finalizing'
          ? 'Publicando vídeo...'
          : `Enviando vídeo... ${percent}%`;
    // `finalizing` é indeterminado de propósito: quem está trabalhando é o
    // servidor, e fingir percentual aqui seria inventar número.
    const indeterminate = direct.phase === 'preparing' || direct.phase === 'finalizing';
    return (
      <div className="border-2 border-dashed border-accent/40 rounded-xl p-6" data-testid="video-upload-progress">
        <div className="flex items-center gap-3 mb-2">
          <span className="material-symbols-outlined text-accent animate-pulse">upload</span>
          <span className="text-sm">{directEnabled ? label : `Enviando vídeo... ${percent}%`}</span>
        </div>
        <div className="h-2 bg-surface-tertiary rounded-full overflow-hidden">
          <div
            className={`h-full bg-accent rounded-full transition-all ${indeterminate ? 'animate-pulse' : ''}`}
            style={{ width: indeterminate ? '100%' : `${percent}%` }}
          />
        </div>
        {directEnabled && direct.phase === 'uploading' && (
          <button
            type="button"
            onClick={handleCancel}
            className="mt-3 text-xs text-content-muted hover:text-red-400"
            data-testid="video-upload-cancel"
          >
            Cancelar envio
          </button>
        )}
      </div>
    );
  }

  if (directEnabled && direct.phase === 'error') {
    return (
      <div className="border-2 border-dashed border-red-500/40 rounded-xl p-6" data-testid="video-upload-error">
        <div className="flex items-center gap-2 mb-2 text-sm text-red-400">
          <span className="material-symbols-outlined text-base">error</span>
          O envio de &ldquo;{direct.fileName}&rdquo; foi interrompido.
        </div>
        <div className="flex gap-3">
          {direct.canRetry && (
            <button
              type="button"
              onClick={handleRetry}
              className="text-xs text-accent hover:underline"
              data-testid="video-upload-resume"
            >
              Retomar de onde parou
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="text-xs text-content-muted hover:text-content-primary"
          >
            Escolher outro arquivo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="video-dropzone"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <label
        className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          dragging ? 'border-accent bg-accent/5' : 'border-theme-border hover:border-accent/40'
        }`}
      >
        <span className="material-symbols-outlined text-3xl text-content-muted mb-2 block">cloud_upload</span>
        <p className="text-sm text-content-muted">Arraste ou clique para enviar</p>
        <p className="text-xs text-content-muted/60 mt-1">MP4, WebM, MOV — até {maxLabel}</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          className="hidden"
          data-testid="video-file-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
    </div>
  );
};

export default VideoUploadPanel;
